/**
 * Thread role classifier for the chat history sidebar.
 *
 * Each chat thread is tagged with a short operational role derived from its
 * AI-generated title. The role prefix helps bankers recognise the mode of work
 * at a glance without implying seniority — these are helper-role names, not
 * authority titles. See docs/banker-flow-prototype-spec-2026-04-30.md for the
 * full design rationale and role table.
 *
 * Classification is keyword-based, applied client-side against the Italian
 * thread title. No backend change required.
 */

export type ThreadRole =
  | 'Analista'
  | 'Ricercatore'
  | 'Revisore'
  | 'Assistente'
  | 'Pianificatore'
  | 'Redattore'
  | 'Consulente'

interface RoleRule {
  role: ThreadRole
  keywords: string[]
}

// Rules are evaluated in order; first match wins. More specific roles come first
// to avoid a broad keyword shadowing a precise one (e.g. "scadenza" is Revisore,
// not generic analysis).
const ROLE_RULES: RoleRule[] = [
  {
    role: 'Revisore',
    keywords: [
      'kyc',
      'scadenza',
      'scadenze',
      'compliance',
      'verifica',
      'revisione',
      'adeguatezza',
      'mifid',
      'regolatorio',
      'regolamentare',
    ],
  },
  {
    role: 'Analista',
    keywords: [
      'portfolio',
      'portafoglio',
      'performance',
      'rendimento',
      'posizione',
      'allocazione',
      'esposizione',
      'analisi',
      'resoconto',
      'aum',
      'patrimonio',
      'asset',
      'composizione',
      'distribuzione',
      'concentrazione',
      'rischio',
      'volatilità',
      'benchmark',
      'dati',
    ],
  },
  {
    role: 'Ricercatore',
    keywords: [
      'riassunto',
      'sintesi',
      'panoramica',
      'ricerca',
      'scheda',
      'libro',
      'articolo',
      'overview',
      'sommario',
      'descrizione',
      'informazioni',
      'approfondimento',
    ],
  },
  {
    role: 'Redattore',
    keywords: [
      'proposta',
      'relazione',
      'bozza',
      'redazione',
      'presentazione',
      'testo',
      'lettera formale',
      'documento',
    ],
  },
  {
    role: 'Pianificatore',
    keywords: [
      'priorità',
      'agenda',
      'pianificazione',
      'ordine del giorno',
      'mattutino',
      'mattutine',
      'settimanale',
      'obiettivi',
      'prossimi passi',
      'riunione',
    ],
  },
  {
    role: 'Assistente',
    keywords: [
      'email',
      'mail',
      'appuntamento',
      'incontro',
      'promemoria',
      'comunicazione',
      'messaggio',
      'organizzare',
      'contattare',
      'convocazione',
      'contatto',
      'corrispondenza',
    ],
  },
]

const FALLBACK_ROLE: ThreadRole = 'Consulente'

export function classifyThreadRole(title: string): ThreadRole {
  if (!title.trim()) return FALLBACK_ROLE
  const lower = title.toLowerCase()
  for (const { role, keywords } of ROLE_RULES) {
    if (keywords.some((kw) => lower.includes(kw))) return role
  }
  return FALLBACK_ROLE
}

/**
 * Returns the thread title with its inferred role label on the first line and
 * the original title on the second line, separated by a newline. The sidebar
 * relies on `white-space: pre-line` plus a `::first-line` rule in
 * `DesignerSidebar.module.scss` to render the role as a small eyebrow above
 * the title. Falls back to `untitled` when the title is empty (no role line).
 *
 * Example: "Analista\nResoconto posizione e performance cliente"
 */
export function formatThreadTitle(title: string | null | undefined, untitled = 'Nuova conversazione'): string {
  const resolved = title?.trim() || untitled
  if (resolved === untitled) return resolved
  const role = classifyThreadRole(resolved)
  return `${role}\n${resolved}`
}
